import { map, catchError } from 'rxjs/operators';
/* Permission Checker Service */
/* @author Mahalingam Ganesan */
/* @Filename permission-checker.service.ts */

/*
    Handles the operations related to the entitlement data management
    and permission checks at the client.

    Usecases:
    1. Retrieve the user roles from a server and persist in the client
    2. Provide APIs to check whether the current user has a certain role or a combination of roles
    3. Clean up the data in the client side

    Assumptions:
    1. There will be a REST service that will provide the list of available roles
    2. The format of response would be as follows:
        "permissions": [ "ROLE_1", "ROLE_2", "ROLE_3", "ROLE_4"]

    3. The service will rely on the authentication token to fetch the roles and will not use User ID
*/

import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { IAuthorizationService } from '../interfaces';
import { LogService } from './log.service';
import { PermissionsServiceDefinition } from '../service-definitions/permissions-service-definition';
import { ServiceDefinition } from '../service-definitions/service-definition';
import { ServiceRouter } from '../utils/service-router';
import { of } from 'rxjs';

@Injectable()
export class PermissionCheckerService implements IAuthorizationService {

    public roleStore: any [] = null; /* To store the roles */
    public serviceUrl: string;
    private retrievalStatus = false;

    constructor(private http: HttpClient, private logService: LogService, private serviceRouter: ServiceRouter,
                @Inject('AppSettings') private appSettings: any) {
        if (this.appSettings && this.appSettings.entitlementJsonObject) {
            this.logService.info('PermissionChecker :', this.appSettings.entitlementJsonObject);
            const data = JSON.parse(this.appSettings.entitlementJsonObject);
            if (typeof data !== 'undefined' && data.response !== undefined &&
                    data.response.permissions !== undefined &&
                    data.response.permissions.length !== undefined ) {
                    this.roleStore = data.response.permissions;
                    this.logService.info('PermissionChecker : Fetching the user roles - SUCCESS', this.roleStore);
        }}
        this.serviceUrl = this.serviceRouter.getServiceRouteURL(PermissionsServiceDefinition.getInstance());
    }

    /**
     * This method sets the proper serviceUrl prior to making a call to it.
     * It first looks at the appSettings to see if an authorizationUrl is set. This
     * takes the highest priority.
     * If no authorizationUrl is defined in the appSettings, then it looks to see
     * if a ServiceDefinition instance is passed from the application.
     * If none of the conditions are met, it will default to the nxp permissions url.
     *
     * @param serviceDefinitionInstance
     * ServiceDefinition Config
     *
     * Returns fetched roles.
     */
    init(serviceDefinitionInstance?: ServiceDefinition) {
        if (this.appSettings && this.appSettings.authorizationUrl) {
            this.serviceUrl = this.appSettings.authorizationUrl;
        } else if (serviceDefinitionInstance) {
            this.serviceUrl = this.serviceRouter.getServiceRouteURL(serviceDefinitionInstance);
        }
        /* Fetch the roles on service initialization */
        return this.fetchUserRoles();
    }

    /*
        Fetches user role from server.
        Internal function, not to be invoked by any consumers directly.
        Throws error with appropriate details on failure and resets the roles on failure.
    */
    fetchUserRoles(): Promise<any> {
        if (this.roleStore) {
            this.retrievalStatus = true;
            this.logService.info('PermissionChecker : Fetching the user roles - RolesData is available');
            return of(this.roleStore).toPromise();
        }

        /*
            If we reach here, it means either this is first request or
            we cleaned up local data and refreshing the roles data from server.
        */
        return this.http.get(this.serviceUrl, { responseType: 'json' })
            .pipe(
                map((data: any) => {
                    if (typeof data !== 'undefined' && data.response !== undefined &&
                        data.response.permissions !== undefined &&
                        data.response.permissions.length !== undefined ) {
                        this.roleStore = data.response.permissions;
                        this.retrievalStatus = true;
                        this.logService.info('PermissionChecker : Fetching the user roles - SUCCESS');
                    } else {
                        /* Set the rolesStore to NULL as the method may be called multiple times */
                        this.roleStore = null;
                        this.logService.error(`ERROR: PermissionChecker: Could not fetch the user roles. Error code: ${data.errorCode}`);
                    }
                    return this.roleStore;
                }),
                catchError((error) => {
                    this.roleStore = null;
                    this.logService.error('ERROR: PermissionChecker : ' , error);
                    return of(this.roleStore);
                })
            ).toPromise();
    }

    checkRetrievalStatus(): boolean {
        return this.retrievalStatus;
    }

    /*
        A setRoles() method was not implemented as no external entity should be
        able to overwrite the roles data.
    */

    /*
        Below method is what should be used by the any of the consumers.
        This would take an entitlement expression as input, parse the expression
        and evaluate it to return the status.
    */
    isEntitled(roleExpression: string): boolean {

        /*
        The expressions passed to this method are expected to be have
        a space between every operator and role value.

        Below are some samples:

            role1 && role2
            role1 || ( role2 && role3 )
            ( role1 && role2 ) || (role3 && role4 ) && ! role5

            Every role and operator must be separated by a space as shown above.
            This is to keep the parsing logic simple enough.

            where role1 and role2 would be like 121 or imaging.documents.allowRead.

            If the action (like 'allowRead') is missing, then by default, it will be taken as 'allowRead'.
        */
        let element: any;
        let roleElement: any;
        const operatorArray: string[] = ['&&', '||', '!', '(', ')', '((', '))'];
        let roleExpressionArray: string[];
        let roleExpressionArrayLength: number;
        let entIdentifier: string;
        let roleStatus: any;
        let modifiedRoleExpression: any;

        /* Below should not happen, but keeping it as a sanity check */
        if (!this.roleStore) {
            /* RoleStore is null mostly because the Portal Service call failed.
               So, return 'false' for any entitlement check */
            this.logService.error(`ERROR: PermissionChecker : roleStore is null`);
            return false;
        }

        /*
            If roleExpression is null/undefined, return false.
            So, if the call to get permissions failed,
            then none of the entitled features will be available to the user.
        */
        if (!roleExpression) {
            return false;
        }

        // Chop off the leading and trailing blanks
        roleExpression = roleExpression.trim();

        /* Split the roles expression so that we can evaluate each of them */
        if (roleExpression && (roleExpression.length > 0)) {
            roleExpressionArray = roleExpression.split(' ');
            roleExpressionArrayLength = roleExpressionArray.length;
        } else {
            return false;
        }

        if (roleExpressionArrayLength === 1) {
            /*
                Below piece is to handle wierd case of someone just passing a
                valid operator alone as in ' || '.
                If it is an operator value alone, then respond with false.
            */
            const operatorElement = operatorArray.find(item => item === roleExpression);
            if (operatorElement) {
                return false;
            }

            roleExpression = '( ' +  roleExpression + ' )';
            roleExpressionArray = roleExpression.split(' ');
        }

        /* Iterate through the array and create the expression by replacing  the roles with true/false */
        for (element in roleExpressionArray) {
            if (operatorArray.indexOf(roleExpressionArray[element]) === -1) {
                entIdentifier = roleExpressionArray[element];
                roleElement = this.roleStore.find(item => item === entIdentifier);
                if (roleElement === undefined) {
                    roleExpressionArray[element] = 'false';
                } else {
                    roleExpressionArray[element] = 'true';
                }
            }
        }

        /* The roles expression is not modified after evaluating individual roles */
        modifiedRoleExpression = roleExpressionArray.join('');

        /*
            Evaluate the modified expression to a final result.
            If there is an issue in evaluation, then return false.
            Most of the typical error cases are handled already.
            The catch block is to handle the worst case scenario like some config file not loaded.
        */
        try {
            roleStatus = (new Function('return ' + modifiedRoleExpression))();
            return roleStatus;
        } catch (error) {
            this.logService.error('ERROR: PermissionChecker : On evaluating the expression ', error);
            return false;
        }
    }

    /*
        Cleans up the local roles data.
    */
    cleanUp(): void {
        this.roleStore = null;
    }

}
